import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, User, Mail, Phone, Calendar, Clock, CreditCard, FileCheck, Download, CheckCircle, XCircle, Ruler, Trophy, MessageSquare, Bell, Send, X, Camera, Upload, Trash2, Users, MapPin, Hash, Pencil, Save, Banknote, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const statusLabels = {
  confirmed: { label: 'Confirme', cls: 'bg-[#00FF00]/15 text-[#00FF00]' },
  pending: { label: 'En attente', cls: 'bg-yellow-500/15 text-yellow-400' },
  pending_onsite: { label: 'A payer sur place', cls: 'bg-blue-500/15 text-blue-400' },
  pending_payment_link: { label: 'Lien envoye', cls: 'bg-purple-500/15 text-purple-400' },
  cancelled: { label: 'Annule', cls: 'bg-red-500/15 text-red-400' },
};

const paymentLabels = {
  online: 'Carte en ligne',
  onsite: 'Sur place',
  credits: 'Credits',
  gift_card: 'Carte cadeau',
  admin: 'Admin',
  pay_by_link: 'Lien de paiement',
};

const paymentDetailLabels = {
  cb: 'CB',
  especes: 'Espèces',
  cheques_vacances: 'Chq. Vacances',
  credits: 'Crédits',
  lien: 'Lien',
  carte_cadeau: 'Carte cadeau',
  sport_decouverte: 'Sport Découverte',
  adrenactive: 'Adrénactive',
  manawa: 'Manawa',
  autres: 'Autres',
};

const gmailComposeUrl = (to, subject) => {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}`;
};

export function BookingDetailModal({ bookingId, open, onClose, onConfirmPayment, API_BASE, activities = [], onRefresh }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [notifyType, setNotifyType] = useState('all');
  const [notifyMsg, setNotifyMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentOtherText, setPaymentOtherText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [changingPayment, setChangingPayment] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const token = localStorage.getItem('rw_token');

  useEffect(() => {
    if (!open || !bookingId) { setBooking(null); setShowNotify(false); return; }
    setLoading(true);
    adminAPI.getBookingDetail(bookingId)
      .then(r => setBooking(r.data))
      .catch(() => toast.error('Impossible de charger les details'))
      .finally(() => setLoading(false));
    adminAPI.getBookingPhotos(bookingId)
      .then(r => setPhotos(r.data.photos || []))
      .catch(() => {});
  }, [bookingId, open]);

  const handleConfirm = async () => {
    if (!booking || !selectedPaymentMethod) return;
    if (selectedPaymentMethod === 'autres' && !paymentOtherText.trim()) { toast.error('Précisez le moyen de paiement'); return; }
    setConfirming(true);
    try {
      const payload = { payment_detail_method: selectedPaymentMethod };
      if (selectedPaymentMethod === 'autres') payload.payment_detail_other = paymentOtherText.trim();
      await onConfirmPayment(booking.id, payload);
      setBooking(prev => ({ ...prev, status: 'confirmed', payment_detail_method: selectedPaymentMethod, payment_detail_other: paymentOtherText.trim() }));
      setShowPaymentConfirm(false);
      setSelectedPaymentMethod('');
      setPaymentOtherText('');
    } catch {} finally { setConfirming(false); }
  };

  const handleChangePaymentMethod = async () => {
    if (!booking || !selectedPaymentMethod) return;
    if (selectedPaymentMethod === 'autres' && !paymentOtherText.trim()) { toast.error('Précisez le moyen de paiement'); return; }
    setConfirming(true);
    try {
      const payload = { payment_detail_method: selectedPaymentMethod };
      if (selectedPaymentMethod === 'autres') payload.payment_detail_other = paymentOtherText.trim();
      await adminAPI.editBooking(booking.id, payload);
      setBooking(prev => ({ ...prev, payment_detail_method: selectedPaymentMethod, payment_detail_other: paymentOtherText.trim() }));
      setChangingPayment(false);
      setSelectedPaymentMethod('');
      setPaymentOtherText('');
      toast.success('Moyen de paiement modifié');
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setConfirming(false); }
  };

  const startEdit = () => {
    setEditForm({
      date: booking.date || '',
      time_slot: booking.time_slot || '',
      duration_minutes: booking.duration_minutes || 30,
      quantity: booking.quantity || 1,
      price: booking.price || 0,
      status: booking.status || 'pending',
      activity_id: booking.activity_id || '',
      payment_method: booking.payment_method || 'onsite',
      notes: booking.notes || '',
    });
    setEditing(true);
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    setCancelling(true);
    try {
      await adminAPI.editBooking(booking.id, { status: 'cancelled' });
      setBooking(prev => ({ ...prev, status: 'cancelled' }));
      setConfirmCancel(false);
      toast.success('Réservation annulée');
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setCancelling(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateBooking(booking.id, editForm);
      toast.success('Reservation modifiee');
      setEditing(false);
      // Reload booking
      const r = await adminAPI.getBookingDetail(bookingId);
      setBooking(r.data);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur modification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteBooking(booking.id);
      toast.success('Reservation supprimee');
      setConfirmDelete(false);
      onClose();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleSendNotification = async () => {
    if (!notifyMsg.trim()) { toast.error('Veuillez ecrire un message'); return; }
    setSending(true);
    try {
      const res = await adminAPI.notifyClient(booking.id, { type: notifyType, message: notifyMsg.trim() });
      toast.success(res.data.message);
      setShowNotify(false);
      setNotifyMsg('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let count = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await adminAPI.uploadBookingPhoto(booking.id, formData);
        count++;
      } catch (err) {
        toast.error(`Erreur: ${file.name}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} photo(s) uploadee(s) avec filigrane`);
      adminAPI.getBookingPhotos(booking.id).then(r => setPhotos(r.data.photos || []));
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await adminAPI.deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo supprimee');
    } catch { toast.error('Erreur suppression'); }
  };

  const st = booking ? statusLabels[booking.status] || statusLabels.pending : null;

  return (
    <>
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading text-lg">Details de la reservation</DialogTitle>
            <DialogDescription className="sr-only">Détails et édition de la réservation</DialogDescription>
            {booking && !editing && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={startEdit} className="text-blue-400 hover:text-blue-300 h-8" data-testid="detail-edit-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} className="text-red-400 hover:text-red-300 h-8" data-testid="detail-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#00FF00]" />
          </div>
        )}

        {!loading && booking && (
          <div className="space-y-4 mt-2">

            {/* EDIT MODE */}
            {editing && (
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#00FF00]/30 space-y-3" data-testid="booking-edit-form">
                <h4 className="text-xs font-semibold text-[#00FF00] uppercase tracking-wider mb-2">Modifier la reservation</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-slate-400">Date</Label>
                    <Input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-date" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Creneau</Label>
                    <Input type="time" value={editForm.time_slot} onChange={e => setEditForm({...editForm, time_slot: e.target.value})} className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-time" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Duree (min)</Label>
                    <Input type="number" value={editForm.duration_minutes} onChange={e => setEditForm({...editForm, duration_minutes: parseInt(e.target.value) || 30})} className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-duration" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Places</Label>
                    <Input type="number" value={editForm.quantity} onChange={e => setEditForm({...editForm, quantity: parseInt(e.target.value) || 1})} className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-quantity" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Prix (EUR)</Label>
                    <Input type="number" step="0.01" value={editForm.price} onChange={e => setEditForm({...editForm, price: parseFloat(e.target.value) || 0})} className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-price" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Statut</Label>
                    <Select value={editForm.status} onValueChange={v => setEditForm({...editForm, status: v})}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                        <SelectItem value="confirmed">Confirme</SelectItem>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="pending_onsite">A payer sur place</SelectItem>
                        <SelectItem value="pending_payment_link">Lien envoye</SelectItem>
                        <SelectItem value="cancelled">Annule</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400">Paiement</Label>
                    <Select value={editForm.payment_method} onValueChange={v => setEditForm({...editForm, payment_method: v})}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-payment"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                        <SelectItem value="online">Carte en ligne</SelectItem>
                        <SelectItem value="onsite">Sur place</SelectItem>
                        <SelectItem value="credits">Credits</SelectItem>
                        <SelectItem value="gift_card">Carte cadeau</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="pay_by_link">Lien de paiement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {activities.length > 0 && (
                    <div>
                      <Label className="text-[10px] text-slate-400">Activite</Label>
                      <Select value={editForm.activity_id} onValueChange={v => setEditForm({...editForm, activity_id: v})}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8" data-testid="edit-activity"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                          {activities.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">Notes</Label>
                  <textarea value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} rows={2} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00FF00]/50 resize-none" data-testid="edit-notes" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs" data-testid="edit-save-btn">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)} className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs" data-testid="edit-cancel-btn">
                    Annuler
                  </Button>
                </div>
              </div>
            )}

            {/* Top bar: Activity + Status + Price */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-white" data-testid="detail-activity-name">{booking.activity_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {booking.site_name && <span className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="w-3 h-3" />{booking.site_name}</span>}
                    <span className="flex items-center gap-1 text-[10px] text-slate-600"><Hash className="w-3 h-3" />{booking.id?.slice(0, 8)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Badge className={`${st.cls} border-0 text-xs`} data-testid="detail-status">{st.label}</Badge>
                  <span className="text-lg font-bold text-[#00FF00]" data-testid="detail-price">{booking.price}EUR</span>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniInfo icon={Calendar} label="Date" value={booking.date} testId="detail-date" />
                <MiniInfo icon={Clock} label="Creneau" value={`${booking.time_slot} - ${booking.duration_minutes}min`} testId="detail-time" />
                <MiniInfo icon={CreditCard} label="Paiement" value={paymentLabels[booking.payment_method] || booking.payment_method} testId="detail-payment" />
                <MiniInfo icon={Users} label="Places" value={`${booking.quantity} participant${booking.quantity > 1 ? 's' : ''}`} testId="detail-quantity" />
              </div>

              {/* Confirm payment action - PLACED HIGH for quick access */}
            {booking.status === 'pending_onsite' && !showPaymentConfirm && (
              <Button onClick={() => setShowPaymentConfirm(true)} className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="detail-confirm-payment">
                <CheckCircle className="w-4 h-4 mr-2" /> Confirmer le paiement
              </Button>
            )}
            {booking.status === 'pending_onsite' && showPaymentConfirm && (
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#00FF00]/30 space-y-3" data-testid="payment-method-dialog">
                <h4 className="text-xs font-semibold text-[#00FF00] uppercase tracking-wider">Moyen de paiement</h4>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'cb', label: 'CB', icon: CreditCard },
                    { key: 'especes', label: 'Espèces', icon: Banknote },
                    { key: 'cheques_vacances', label: 'Chq. Vacances', icon: FileCheck },
                    { key: 'credits', label: 'Crédits', icon: Wallet },
                    { key: 'lien', label: 'Lien', icon: Send },
                    { key: 'carte_cadeau', label: 'Carte cadeau', icon: CreditCard },
                    { key: 'sport_decouverte', label: 'Sport Découverte', icon: Hash },
                    { key: 'adrenactive', label: 'Adrénactive', icon: Hash },
                    { key: 'manawa', label: 'Manawa', icon: Hash },
                    { key: 'autres', label: 'Autres', icon: Hash },
                  ].map(m => (
                    <button
                      key={m.key}
                      onClick={() => setSelectedPaymentMethod(m.key)}
                      className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                        selectedPaymentMethod === m.key
                          ? 'bg-[#00FF00]/20 border-2 border-[#00FF00] text-[#00FF00]'
                          : 'bg-[#1A1A1A] border border-[#2A2A2A] text-slate-400 hover:border-[#444] hover:text-white'
                      }`}
                      data-testid={`payment-method-${m.key}`}
                    >
                      <m.icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  ))}
                </div>
                {selectedPaymentMethod === 'autres' && (
                  <Input value={paymentOtherText} onChange={e => setPaymentOtherText(e.target.value)} placeholder="Préciser le moyen de paiement..." className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs" data-testid="payment-other-input" />
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handleConfirm}
                    disabled={!selectedPaymentMethod || confirming || (selectedPaymentMethod === 'autres' && !paymentOtherText.trim())}
                    className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs disabled:opacity-50"
                    data-testid="confirm-payment-submit"
                  >
                    {confirming ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                    Valider ({selectedPaymentMethod === 'autres' ? paymentOtherText || '...' : paymentDetailLabels[selectedPaymentMethod] || '...'})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowPaymentConfirm(false); setSelectedPaymentMethod(''); setPaymentOtherText(''); }}
                    className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs"
                    data-testid="cancel-payment-confirm"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}

              {/* Payment detail info */}
              {booking.status === 'confirmed' && booking.payment_detail_method && !changingPayment && (
                <div className="mt-2 flex items-center gap-2 bg-[#00FF00]/10 border border-[#00FF00]/30 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-[#00FF00]/15 transition-colors" data-testid="payment-detail-info" onClick={() => { setChangingPayment(true); setSelectedPaymentMethod(booking.payment_detail_method); setPaymentOtherText(booking.payment_detail_other || ''); }}>
                  <Wallet className="w-4 h-4 text-[#00FF00]" />
                  <span className="text-xs text-[#00FF00] font-bold">
                    Payé {booking.payment_detail_method === 'autres' ? (booking.payment_detail_other || 'Autres') : (paymentDetailLabels[booking.payment_detail_method] || booking.payment_detail_method)}
                  </span>
                  <Pencil className="w-3 h-3 text-[#00FF00]/50 ml-auto" />
                  {booking.confirmed_at && (
                    <span className="text-[10px] text-slate-500">
                      le {new Date(booking.confirmed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
              {booking.status === 'confirmed' && !booking.payment_detail_method && !changingPayment && booking.payment_method === 'onsite' && (
                <Button onClick={() => setChangingPayment(true)} variant="outline" className="mt-2 w-full border-amber-500/30 text-amber-400 bg-transparent hover:bg-amber-500/10 text-xs" data-testid="add-payment-detail-btn">
                  <Wallet className="w-3 h-3 mr-1" /> Préciser le moyen de paiement
                </Button>
              )}
              {/* Change payment method dialog for confirmed bookings */}
              {changingPayment && (
                <div className="mt-2 bg-[#0A0A0A] rounded-xl p-4 border border-amber-500/30 space-y-3" data-testid="change-payment-dialog">
                  <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Modifier le moyen de paiement</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'cb', label: 'CB', icon: CreditCard },
                      { key: 'especes', label: 'Espèces', icon: Banknote },
                      { key: 'cheques_vacances', label: 'Chq. Vacances', icon: FileCheck },
                      { key: 'credits', label: 'Crédits', icon: Wallet },
                      { key: 'lien', label: 'Lien', icon: Send },
                      { key: 'carte_cadeau', label: 'Carte cadeau', icon: CreditCard },
                      { key: 'sport_decouverte', label: 'Sport Découverte', icon: Hash },
                      { key: 'adrenactive', label: 'Adrénactive', icon: Hash },
                      { key: 'manawa', label: 'Manawa', icon: Hash },
                      { key: 'autres', label: 'Autres', icon: Hash },
                    ].map(m => (
                      <button key={m.key} onClick={() => setSelectedPaymentMethod(m.key)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${selectedPaymentMethod === m.key ? 'bg-amber-500/20 border-2 border-amber-400 text-amber-400' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-slate-400 hover:border-[#444]'}`}
                        data-testid={`change-pm-${m.key}`}>
                        <m.icon className="w-3 h-3" /> {m.label}
                      </button>
                    ))}
                  </div>
                  {selectedPaymentMethod === 'autres' && (
                    <Input value={paymentOtherText} onChange={e => setPaymentOtherText(e.target.value)} placeholder="Préciser..." className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs" data-testid="change-pm-other-input" />
                  )}
                  <div className="flex gap-2">
                    <Button onClick={handleChangePaymentMethod} disabled={!selectedPaymentMethod || confirming || (selectedPaymentMethod === 'autres' && !paymentOtherText.trim())} className="flex-1 bg-amber-500 text-black hover:bg-amber-600 font-bold text-xs disabled:opacity-50" data-testid="save-payment-change">
                      {confirming ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer
                    </Button>
                    <Button variant="outline" onClick={() => { setChangingPayment(false); setSelectedPaymentMethod(''); setPaymentOtherText(''); }} className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs">Annuler</Button>
                  </div>
                </div>
              )}
              {booking.transaction && (
                <div className="mt-2 bg-[#1A1A1A] rounded-lg px-3 py-2 border border-[#2A2A2A]" data-testid="payment-transaction-info">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Transaction</span>
                    <Badge className={`border-0 text-[10px] ${booking.transaction.payment_status === 'paid' ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-yellow-500/15 text-yellow-400'}`}>
                      {booking.transaction.payment_status === 'paid' ? 'Payé' : 'En attente'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    <span>{booking.transaction.amount}€</span>
                    {booking.transaction.metadata?.payment_detail_method && (
                      <span className="text-slate-300 font-medium">{paymentDetailLabels[booking.transaction.metadata.payment_detail_method] || booking.transaction.metadata.payment_detail_method}</span>
                    )}
                    <span className="ml-auto text-[10px] text-slate-600">{new Date(booking.transaction.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              )}

              {/* Created at */}
              {booking.created_at && (
                <p className="text-[10px] text-slate-600 mt-2">
                  Creee le {new Date(booking.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>

            {/* Client section */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Client</h4>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#00FF00]/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-[#00FF00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm" data-testid="detail-client-name">{booking.user_name || booking.client_name}</p>
                  <p className="text-xs text-slate-400" data-testid="detail-client-email">{booking.user_email || booking.client_email}</p>
                  {booking.user_phone && <p className="text-xs text-slate-500" data-testid="detail-client-phone">{booking.user_phone}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {booking.user_age && <Badge className="bg-[#2A2A2A] text-slate-300 border-0 text-[10px]">{booking.user_age} ans</Badge>}
                  {booking.user_size && <Badge className="bg-[#2A2A2A] text-slate-300 border-0 text-[10px]"><Ruler className="w-3 h-3 mr-0.5" />{booking.user_size}</Badge>}
                  {booking.user_level && <Badge className="bg-[#2A2A2A] text-slate-300 border-0 text-[10px]"><Trophy className="w-3 h-3 mr-0.5" />{booking.user_level}</Badge>}
                </div>
              </div>
              {/* Contact buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#2A2A2A]">
                {booking.user_phone && (
                  <>
                    <a href={`tel:${booking.user_phone}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/20 transition-colors" data-testid="detail-call-btn">
                      <Phone className="w-3 h-3" /> Appeler
                    </a>
                    <a href={`sms:${booking.user_phone}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs font-medium hover:bg-purple-500/20 transition-colors" data-testid="detail-sms-btn">
                      <MessageSquare className="w-3 h-3" /> SMS
                    </a>
                    <a href={`https://wa.me/${booking.user_phone.replace(/[^0-9]/g, '').replace(/^0/, '33')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors" data-testid="detail-whatsapp-btn">
                      <MessageSquare className="w-3 h-3" /> WhatsApp
                    </a>
                  </>
                )}
                <a href={gmailComposeUrl(booking.user_email || booking.client_email, `Votre reservation Riding Watt - ${booking.activity_name} du ${booking.date}`)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors" data-testid="detail-email-btn">
                  <Mail className="w-3 h-3" /> Email
                </a>
              </div>
            </div>

            {/* Two columns: Contract + Participants */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Contract */}
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]" data-testid="detail-contract-section">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Contrat</h4>
                {booking.contract_signed ? (
                  <div>
                    <div className="flex items-center gap-2 text-[#00FF00] text-sm mb-2">
                      <FileCheck className="w-4 h-4" />
                      <span className="font-medium">Signe</span>
                      {booking.contract?.signed_at && (
                        <span className="text-[10px] text-slate-500 ml-auto">
                          {new Date(booking.contract.signed_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    {booking.contract?.pdf_path && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => window.open(`${API_BASE}/api/admin/bookings/${booking.id}/contract/pdf?token=${token}`, '_blank')}
                          className="flex-1 text-xs text-center text-[#00FF00] hover:bg-[#00FF00]/10 py-1.5 rounded-lg border border-[#00FF00]/20 flex items-center justify-center gap-1"
                          data-testid="detail-view-pdf"
                        >
                          <FileCheck className="w-3 h-3" /> Voir PDF
                        </button>
                        <button
                          onClick={() => {
                            const w = window.open(`${API_BASE}/api/admin/bookings/${booking.id}/contract/pdf?token=${token}`, '_blank');
                            if (w) setTimeout(() => w.print(), 1000);
                          }}
                          className="flex-1 text-xs text-center text-blue-400 hover:bg-blue-500/10 py-1.5 rounded-lg border border-blue-500/20 flex items-center justify-center gap-1"
                          data-testid="detail-print-pdf"
                        >
                          <Download className="w-3 h-3" /> Imprimer
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-400 text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>Non signe</span>
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]" data-testid="detail-participants-section">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Participants {booking.participants?.length > 0 ? `(${booking.participants.length})` : ''}
                </h4>
                {booking.participants && booking.participants.length > 0 ? (
                  <div className="space-y-1.5">
                    {booking.participants.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg p-2 border border-[#2A2A2A]">
                        <div className="w-6 h-6 rounded-full bg-[#00FF00]/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-[#00FF00]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{p.first_name} {p.last_name}</p>
                          <div className="flex gap-2 text-[9px] text-slate-500">
                            {p.age && <span>{p.age}ans</span>}
                            {p.gabarit && <span>{p.gabarit}</span>}
                            {p.level && <span>{p.level}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-400 text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>Non enregistres</span>
                  </div>
                )}
              </div>
            </div>

            {/* Photos Section */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]" data-testid="detail-photos-section">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Photos ({photos.length})
                </h4>
                <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${uploading ? 'bg-[#2A2A2A] text-slate-500' : 'bg-[#00FF00]/10 border border-[#00FF00]/30 text-[#00FF00] hover:bg-[#00FF00]/20'}`}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {uploading ? 'Upload...' : 'Ajouter'}
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" disabled={uploading} data-testid="photo-upload-input" />
                </label>
              </div>
              {photos.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {photos.map(p => (
                    <div key={p.id} className="relative group rounded-lg overflow-hidden bg-[#1A1A1A] border border-[#2A2A2A]">
                      <img src={adminAPI.getPhotoUrl(p.id)} alt={p.original_filename} className="w-full h-20 object-cover" loading="lazy" />
                      <button onClick={() => handleDeletePhoto(p.id)} className="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5 text-[8px] text-slate-300 truncate">
                        {p.original_filename}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 text-center py-2">Aucune photo. Uploadez des photos pour le client.</p>
              )}
            </div>

            {/* Notification Section */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Notification</h4>
              </div>
              {!showNotify ? (
                <Button
                  onClick={() => setShowNotify(true)}
                  variant="outline"
                  className="w-full border-[#00FF00]/30 text-[#00FF00] hover:bg-[#00FF00]/10 hover:text-[#00FF00] bg-transparent"
                  data-testid="detail-notify-btn"
                >
                  <Bell className="w-4 h-4 mr-2" /> Notifier le client
                </Button>
              ) : (
                <div className="space-y-3" data-testid="notify-form">
                  <div className="flex gap-2">
                    {[
                      { key: 'all', label: 'Push + Email', icon: Bell },
                      { key: 'push', label: 'Push seul', icon: Bell },
                      { key: 'email', label: 'Email seul', icon: Mail },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setNotifyType(opt.key)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                          notifyType === opt.key
                            ? 'bg-[#00FF00]/20 border border-[#00FF00]/50 text-[#00FF00]'
                            : 'bg-[#1A1A1A] border border-[#2A2A2A] text-slate-400 hover:text-slate-200'
                        }`}
                        data-testid={`notify-type-${opt.key}`}
                      >
                        <opt.icon className="w-3 h-3" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={notifyMsg}
                    onChange={e => setNotifyMsg(e.target.value)}
                    placeholder="Votre message au client..."
                    rows={3}
                    className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00FF00]/50 resize-none"
                    data-testid="notify-message-input"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Rappel : votre session est demain !',
                      'Merci de prevoir maillot et creme solaire.',
                      'Conditions meteo : session maintenue.',
                      'Session reportee, nous vous recontactons.',
                    ].map(msg => (
                      <button
                        key={msg}
                        onClick={() => setNotifyMsg(msg)}
                        className="px-2 py-1 rounded-md bg-[#2A2A2A] text-[10px] text-slate-400 hover:text-white hover:bg-[#3A3A3A] transition-colors"
                      >
                        {msg}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSendNotification}
                      disabled={sending || !notifyMsg.trim()}
                      className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs"
                      data-testid="notify-send-btn"
                    >
                      {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                      Envoyer
                    </Button>
                    <Button
                      onClick={() => { setShowNotify(false); setNotifyMsg(''); }}
                      variant="outline"
                      className="border-[#2A2A2A] text-slate-400 hover:text-white bg-transparent text-xs"
                      data-testid="notify-cancel-btn"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {booking.notes && (
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Notes</h4>
                <p className="text-sm text-slate-300">{booking.notes}</p>
              </div>
            )}

            {/* Cancel booking button - visible for non-cancelled bookings */}
            {booking.status !== 'cancelled' && !confirmCancel && (
              <Button onClick={() => setConfirmCancel(true)} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent font-medium" data-testid="cancel-booking-btn">
                <XCircle className="w-4 h-4 mr-2" /> Annuler la réservation
              </Button>
            )}
            {confirmCancel && (
              <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/30 space-y-3" data-testid="cancel-booking-confirm">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-400">Annuler cette réservation ?</p>
                    <p className="text-xs text-slate-500 mt-1">
                      <strong className="text-white">{booking.client_name}</strong> — {booking.activity_name} le {booking.date} à {booking.time_slot}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCancelBooking} disabled={cancelling} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold text-xs" data-testid="confirm-cancel-booking-btn">
                    {cancelling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                    Oui, annuler
                  </Button>
                  <Button onClick={() => setConfirmCancel(false)} variant="outline" className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs">Non, garder</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Supprimer cette reservation ?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            Cette action est irreversible. La reservation de <strong className="text-white">{booking?.client_name || booking?.user_name}</strong> pour <strong className="text-white">{booking?.activity_name}</strong> le <strong className="text-white">{booking?.date}</strong> sera definitivement supprimee.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-[#2A2A2A] text-slate-300 border-[#333] hover:bg-[#333]">Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700 font-bold">Supprimer</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function MiniInfo({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-[#1A1A1A] rounded-lg p-2.5 border border-[#2A2A2A]">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="w-3 h-3 text-slate-500" />
        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xs font-semibold text-white" data-testid={testId}>{value}</p>
    </div>
  );
}
